import React, { useState, useMemo } from 'react';
import {
  Box, Text, VStack, HStack, Flex, Modal, ModalOverlay,
  ModalContent, ModalBody, SimpleGrid, useBreakpointValue,
} from '@chakra-ui/react';
import { ACHIEVEMENTS } from '../hooks/useAchievements';

const ORANGE = '#F7931A';
const ORANGE_DIM = 'rgba(247, 147, 26, 0.7)';
const ORANGE_GLOW = 'rgba(247, 147, 26, 0.15)';
const GOLD = '#FFD700';


const BRACKET_SIZE = '8px';
const BRACKET_THICK = '1px';

const BracketPanel = ({ children, accentColor = ORANGE_DIM, cornerInset = '4px', ...props }) => (
  <Box
    position="relative"
    p={{ base: '10px', md: '14px' }}
    pointerEvents="auto"
    bg="rgba(20, 20, 20, 0.6)"
    backdropFilter="blur(6px)"
    {...props}
    _after={{
      content: '""',
      position: 'absolute',
      inset: 0,
      background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.015) 2px, rgba(255,255,255,0.015) 4px)',
      pointerEvents: 'none',
      zIndex: 1,
    }}
  >
    <Box position="absolute" top={cornerInset} left={cornerInset} w={BRACKET_SIZE} h={BRACKET_SIZE}
      borderTop={BRACKET_THICK} borderLeft={BRACKET_THICK} borderColor={accentColor} />
    <Box position="absolute" top={cornerInset} right={cornerInset} w={BRACKET_SIZE} h={BRACKET_SIZE}
      borderTop={BRACKET_THICK} borderRight={BRACKET_THICK} borderColor={accentColor} />
    <Box position="absolute" bottom={cornerInset} left={cornerInset} w={BRACKET_SIZE} h={BRACKET_SIZE}
      borderBottom={BRACKET_THICK} borderLeft={BRACKET_THICK} borderColor={accentColor} />
    <Box position="absolute" bottom={cornerInset} right={cornerInset} w={BRACKET_SIZE} h={BRACKET_SIZE}
      borderBottom={BRACKET_THICK} borderRight={BRACKET_THICK} borderColor={accentColor} />
    <Box position="relative" zIndex={2}>{children}</Box>
  </Box>
);

const LockIcon = () => (
  <Box as="svg" viewBox="0 0 24 24" w="12px" h="12px" fill="currentColor" opacity={0.4}>
    <path d="M18 10h-1V7c0-2.76-2.24-5-5-5S7 4.24 7 7v3H6a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2v-8a2 2 0 00-2-2zM9 7c0-1.66 1.34-3 3-3s3 1.34 3 3v3H9V7zm3 10c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/>
  </Box>
);

const CheckIcon = () => (
  <Box as="svg" viewBox="0 0 24 24" w="12px" h="12px" fill={ORANGE}>
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
  </Box>
);

const AchievementCard = ({ achievement, unlocked, index }) => {
  const fmtDate = (ts) => {
    const d = new Date(ts);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  return (
    <Box
      position="relative"
      bg={unlocked ? 'rgba(247, 147, 26, 0.08)' : 'rgba(255, 255, 255, 0.04)'}
      border="1px solid"
      borderColor={unlocked ? ORANGE + '44' : 'whiteAlpha.200'}
      overflow="hidden"
      transition="all 0.2s"
      _hover={unlocked ? { borderColor: ORANGE + '77', bg: 'rgba(247, 147, 26, 0.12)' } : {}}
    >
      <Flex p="10px" gap={3} align="flex-start">
        {/* Number badge */}
        <Flex
          w="24px" h="24px" minW="24px"
          align="center" justify="center"
          border="1px solid"
          borderColor={unlocked ? ORANGE + '44' : 'whiteAlpha.100'}
          bg={unlocked ? ORANGE + '11' : 'transparent'}
          mt="1px"
        >
          <Text
            fontSize="10px" fontFamily="mono" fontWeight="700"
            color={unlocked ? ORANGE : 'whiteAlpha.300'}
            lineHeight="1"
          >
            {String(index + 1).padStart(2, '0')}
          </Text>
        </Flex>

        {/* Content */}
        <VStack spacing="2px" align="stretch" flex={1} minW={0}>
          <HStack spacing={1} align="center">
            {unlocked ? <CheckIcon /> : <LockIcon />}
            <Text
              fontSize="12px" fontFamily="mono" fontWeight="700"
              letterSpacing="0.1em"
              color={unlocked ? 'white' : 'whiteAlpha.300'}
              textShadow={unlocked ? `0 0 10px ${ORANGE}33` : 'none'}
              noOfLines={1}
            >
              {unlocked ? achievement.name : '???'}
            </Text>
          </HStack>
          <Text
            fontSize="10px" fontFamily="mono"
            color={unlocked ? 'whiteAlpha.600' : 'whiteAlpha.200'}
            letterSpacing="0.03em" lineHeight="1.4"
          >
            {unlocked ? achievement.description : '???'}
          </Text>
          {unlocked && (
            <Text fontSize="9px" fontFamily="mono" color="whiteAlpha.600" letterSpacing="0.08em">
              {fmtDate(unlocked.unlockedAt)}
            </Text>
          )}
        </VStack>
      </Flex>
    </Box>
  );
};

const PagBtn = ({ children, disabled, onClick }) => (
  <Box
    as="button"
    px="10px" py="4px"
    fontSize="10px" fontFamily="mono" fontWeight="700"
    letterSpacing="0.15em"
    color={disabled ? 'whiteAlpha.200' : 'whiteAlpha.700'}
    cursor={disabled ? 'default' : 'pointer'}
    border="1px solid"
    borderColor={disabled ? 'whiteAlpha.100' : 'whiteAlpha.300'}
    bg="transparent"
    transition="all 0.15s"
    _hover={disabled ? {} : { color: ORANGE, borderColor: ORANGE_DIM, bg: ORANGE_GLOW }}
    onClick={disabled ? undefined : onClick}
  >
    {children}
  </Box>
);

export const AchievementsModal = ({ isOpen, onClose, achievements, count, allComplete }) => {
  const [page, setPage] = useState(0);
  const perPage = useBreakpointValue({ base: 6, md: 9 }) || 9;
  const cols = useBreakpointValue({ base: 2, md: 3 }) || 3;
  const totalPages = useMemo(() => Math.ceil(ACHIEVEMENTS.length / perPage), [perPage]);
  const pageItems = useMemo(() => {
    const start = page * perPage;
    return ACHIEVEMENTS.slice(start, start + perPage).map((a, i) => ({ ...a, globalIndex: start + i }));
  }, [page, perPage]);

  const ac = allComplete ? GOLD : ORANGE;
  const pct = Math.round((count / ACHIEVEMENTS.length) * 100);

  // Reset page when modal opens
  const prevOpen = React.useRef(isOpen);
  React.useEffect(() => {
    if (isOpen && !prevOpen.current) setPage(0);
    prevOpen.current = isOpen;
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size={{ base: 'full', md: '3xl' }}>
      <ModalOverlay bg="blackAlpha.900" backdropFilter="blur(8px)" />
      <ModalContent bg="#0d0d0d" border="1px solid" borderColor={allComplete ? GOLD + '66' : ORANGE + '44'} borderRadius={0} mx={{ base: 0, md: 4 }}>
        <ModalBody py={5} px={5}>
          <BracketPanel accentColor={ac} p={{ base: '14px', md: '20px' }}>
            <VStack spacing={5} align="stretch">

              {/* Header */}
              <Flex justify="space-between" align="center">
                <HStack spacing={3} align="center">
                  <Flex
                    w="32px" h="32px" align="center" justify="center"
                    border="1px solid" borderColor={ac + '55'}
                    bg={ac + '11'}
                  >
                    <Box as="svg" viewBox="0 0 24 24" w="18px" h="18px" fill={ac}>
                      <circle cx="12" cy="8" r="6" />
                      <path d="M8 14h8l1 3H7l1-3z" />
                      <path d="M6 18h12v2H6z" />
                    </Box>
                  </Flex>
                  <VStack spacing={0} align="start">
                    <Text
                      fontSize="13px" fontFamily="mono" fontWeight="700"
                      letterSpacing="0.25em" color={ac}
                      textShadow={`0 0 12px ${ac}44`}
                    >
                      ACHIEVEMENTS
                    </Text>
                    <Text fontSize="10px" fontFamily="mono" color="whiteAlpha.600" letterSpacing="0.1em">
                      {count} of {ACHIEVEMENTS.length} discovered
                    </Text>
                  </VStack>
                </HStack>
                <Box
                  as="button" onClick={onClose}
                  w="28px" h="28px" display="flex" alignItems="center" justifyContent="center"
                  color="whiteAlpha.600"
                  fontSize="16px" lineHeight="1" cursor="pointer"
                  border="1px solid" borderColor="whiteAlpha.100"
                  transition="all 0.15s"
                  _hover={{ color: ORANGE, borderColor: ORANGE_DIM }}
                >
                  &#x2715;
                </Box>
              </Flex>

              {/* Progress bar */}
              <Box>
                <Box w="100%" h="6px" bg="whiteAlpha.100" position="relative" overflow="hidden">
                  <Box
                    h="100%"
                    w={`${(count / ACHIEVEMENTS.length) * 100}%`}
                    bg={ac}
                    transition="width 0.5s ease"
                    boxShadow={`0 0 10px ${ac}55`}
                  />
                </Box>
                <Flex justify="space-between" mt="4px">
                  <Text fontSize="9px" fontFamily="mono" color="whiteAlpha.600" letterSpacing="0.1em">
                    {pct}% COMPLETE
                  </Text>
                  {!allComplete && (
                    <Text fontSize="9px" fontFamily="mono" color="whiteAlpha.600" letterSpacing="0.1em">
                      {ACHIEVEMENTS.length - count} REMAINING
                    </Text>
                  )}
                  {allComplete && (
                    <Text fontSize="9px" fontFamily="mono" color={GOLD} letterSpacing="0.1em" fontWeight="700">
                      ALL UNLOCKED
                    </Text>
                  )}
                </Flex>
              </Box>

              {/* Divider */}
              <Box h="1px" bg="whiteAlpha.100" />

              {/* Achievement grid */}
              <SimpleGrid columns={cols} spacing={2}>
                {pageItems.map(a => (
                  <AchievementCard
                    key={a.id}
                    achievement={a}
                    unlocked={achievements[a.id]}
                    index={a.globalIndex}
                  />
                ))}
              </SimpleGrid>

              {/* Pagination */}
              {totalPages > 1 && (
                <Flex justify="center" align="center" gap={3}>
                  <PagBtn disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                    &#x25C0; PREV
                  </PagBtn>
                  <HStack spacing={1}>
                    {Array.from({ length: totalPages }, (_, i) => (
                      <Box
                        key={i}
                        w="8px" h="8px"
                        bg={i === page ? ac : 'whiteAlpha.200'}
                        cursor="pointer"
                        transition="all 0.15s"
                        boxShadow={i === page ? `0 0 6px ${ac}66` : 'none'}
                        onClick={() => setPage(i)}
                        _hover={{ bg: i === page ? ac : 'whiteAlpha.600' }}
                      />
                    ))}
                  </HStack>
                  <PagBtn disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
                    NEXT &#x25B6;
                  </PagBtn>
                </Flex>
              )}

              {/* Full Node badge */}
              {allComplete && (
                <Flex justify="center" pt={1}>
                  <HStack
                    spacing={3} px="20px" py="8px"
                    border="1px solid" borderColor={GOLD + '66'}
                    bg={`${GOLD}0a`}
                    animation="fullNodeGlow 3s ease-in-out infinite"
                    sx={{
                      '@keyframes fullNodeGlow': {
                        '0%, 100%': { boxShadow: `0 0 12px ${GOLD}22, inset 0 0 12px ${GOLD}0a` },
                        '50%': { boxShadow: `0 0 24px ${GOLD}44, inset 0 0 24px ${GOLD}15` },
                      },
                    }}
                  >
                    <Box as="svg" viewBox="0 0 24 24" w="20px" h="20px" fill={GOLD}>
                      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2z"/>
                    </Box>
                    <Text
                      fontSize="14px" fontFamily="mono" fontWeight="700"
                      letterSpacing="0.3em" color={GOLD}
                      textShadow={`0 0 12px ${GOLD}66`}
                    >
                      FULL NODE
                    </Text>
                  </HStack>
                </Flex>
              )}
            </VStack>
          </BracketPanel>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
